import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { rootLinks } from '../Links';
import styles from './Services.module.scss';
import { useRouter } from 'next/router';
import Link from 'next/link';
import type { Service, ServicesProps } from './Services.type';
import {
    customersServicesNames,
    particiantsServicesNames,
    services,
} from '../_dummyData/services';
import { CheckWhiteIcon } from '../Icons';
import { getResortedTiles } from './utils/getResortedTiles';

export const ServicesTiles: React.FC<ServicesProps> = React.memo(
    ({ activeTab }) => {
        const isBrowser = typeof window !== 'undefined';
        const [windowWidth, setWindowWidth] = useState(
            isBrowser ? window.innerWidth : 1120
        );

        const handleResize = useCallback(() => {
            setWindowWidth(isBrowser ? window.innerWidth : 1120);
        }, [isBrowser]);
        useEffect(() => {
            return window.addEventListener('resize', handleResize);
        }, [handleResize]);
        const customerTiles = useMemo(() => {
            const tiles = customersServicesNames
                .map((name) =>
                    services.find((service) => service.name === name)
                )
                .filter(Boolean) as unknown as Service[];
            return getResortedTiles(tiles, windowWidth);
        }, [windowWidth]);
        const participantTiles = useMemo(() => {
            const tiles = particiantsServicesNames
                .map((name) =>
                    services.find((service) => service.name === name)
                )
                .filter(Boolean) as unknown as Service[];
            return getResortedTiles(tiles, windowWidth);
        }, [windowWidth]);
        const tiles = useMemo(
            () =>
                activeTab === 'participants' ? participantTiles : customerTiles,
            [activeTab, customerTiles, participantTiles]
        );
        const blockHeight = useMemo(() => {
            const tilesLength = tiles.length;
            const maxTileHeight =
                tilesLength > 7 ? 304 : tilesLength < 5 ? 292 : 381;
            if (windowWidth <= 500) {
                return tilesLength * maxTileHeight;
            } else if (windowWidth > 1119) {
                return Math.ceil(tilesLength / 3) * maxTileHeight;
            } else {
                return Math.ceil(tilesLength / 2) * maxTileHeight;
            }
        }, [tiles, windowWidth]);
        const isLastColumnShortest = useMemo(() => {
            if (windowWidth > 1119) {
                const maxTilesInColumn = Math.ceil(tiles.length / 3);
                const lastColumnLength = tiles.length % 3 || maxTilesInColumn;
                return (
                    maxTilesInColumn - 1 > lastColumnLength &&
                    maxTilesInColumn > 2 &&
                    tiles.length > 4
                );
            }
        }, [tiles.length, windowWidth]);

        return (
            <div
                className={styles.tilesWrapper}
                style={{ height: `${blockHeight}px` }}
            >
                {tiles.map((tile, idx) => {
                    const maxTilesInColumn = Math.ceil(tiles.length / 3);
                    const isLeftColumn =
                        idx + 1 <= maxTilesInColumn && windowWidth > 1119;
                    const isRightColumn = isLastColumnShortest
                        ? idx >= tiles.length - 2
                        : idx > maxTilesInColumn * 2 - 1 && windowWidth > 1119;
                    const isFirstRow =
                        (idx === 0 ||
                            idx === maxTilesInColumn ||
                            (isLastColumnShortest
                                ? idx === maxTilesInColumn * 2 - 1
                                : idx === maxTilesInColumn * 2)) &&
                        windowWidth > 1119;
                    const isLastRow =
                        (idx === maxTilesInColumn - 1 ||
                            idx === tiles.length - 1 ||
                            (isLastColumnShortest
                                ? idx === maxTilesInColumn * 2 - 2
                                : idx === maxTilesInColumn * 2 - 1)) &&
                        windowWidth > 1119;
                    return (
                        <>
                            {isLastColumnShortest &&
                                idx === maxTilesInColumn * 2 - 1 && (
                                    <div
                                        className={styles.tileStub}
                                        key={`${tile.name}-stub`}
                                    ></div>
                                )}
                            <div
                                className={styles.tileWrapper}
                                key={tile.name}
                                style={{
                                    ...(isLeftColumn && { borderLeft: 'none' }),
                                    ...(isRightColumn && {
                                        borderRight: 'none',
                                    }),
                                    ...(isFirstRow && { borderTop: 'none' }),
                                    ...(isLastRow && { borderBottom: 'none' }),
                                }}
                            >
                                <div className={styles.tile}>
                                    <h5>Сервис</h5>
                                    <h3>{tile.title}</h3>
                                    <div className={styles.concealableBlock}>
                                        <ul>
                                            {tile.shortPreferences.map(
                                                (pref) => (
                                                    <li
                                                        key={`${tile.name}-${pref.title}`}
                                                    >
                                                        <CheckWhiteIcon />
                                                        {pref.text}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                        <Link
                                            href={`${rootLinks.services.url}/${activeTab}/${tile.id}`}
                                        >
                                            <a className={styles.action}>
                                                Подробнее
                                            </a>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </>
                    );
                })}
            </div>
        );
    }
);
